import { Component, h } from 'preact'
import axios from 'axios'
import Header from '../header'
import IdeationList from './IdeationList'
import IdeationAddEdit from './IdeationAddEdit'
import Modal from '../../components/reactModal'
import ConfirmationModal from '../ui-components/ConfirmationModal'
import AlertComponent from '../ui-components/AlertComponent'
import { IdeaStatus } from './Types'
import ButtonControl from '../button-control'
import tinycolor from 'tinycolor2'
import {csrfToken} from '../utils/csrfToken'

interface Props {}

interface State {
  list: Array<IdeaStatus>,
  newIdeation: IdeaStatus,
  editableIdeationData: IdeaStatus | null,
  showModal: boolean,
  showConfirmation: boolean,
  isLoading: boolean,
  showError: boolean,
  errorMessage: string,
  messageType: string | null,
  message: string,
  deleteItem: number | null
}

function isIdeation (whatever: IdeaStatus | null): whatever is IdeaStatus {
  return !!whatever
}

class IdeationContainer extends Component<Props, State> {
  private csrfToken: string = ''
  constructor (props: Props) {
    super(props)
    this.state = {
      list: [],
      newIdeation: {
        name: '',
        backgroundColor: '#000',
        textColor: '#fff',
        default: false,
        visible: true
      },
      editableIdeationData: null,
      showModal: false,
      showConfirmation: false,
      isLoading: false,
      showError: false,
      deleteItem: null,
      messageType: null,
      message: '',
      errorMessage: ''
    }

    this.csrfToken = csrfToken()

    this.enableModal = this.enableModal.bind(this)
    this.saveIdeation = this.saveIdeation.bind(this)
    this.updateIdeation = this.updateIdeation.bind(this)
    this.shouldDeleteIdeation = this.shouldDeleteIdeation.bind(this)
    this.deleteIdeation = this.deleteIdeation.bind(this)
  }

  async componentDidMount () {
    const { data } = await axios.get('/community/api/ideas/ideaStatuses')
    if (!data.result.length) {
      return
    }
    data.result.forEach((item: IdeaStatus) => {
      item.backgroundColor = `#${item.backgroundColor}`
      item.textColor = `#${item.textColor}`
    })

    this.setState({ list: data.result, newIdeation: Object.assign({}, this.state.newIdeation, { backgroundColor: data.result[0].backgroundColor }) })
  }

  async saveIdeation () {
    if (!this.state.editableIdeationData) {
      return
    }
    let newData = this.state.editableIdeationData
    if (isIdeation(this.state.editableIdeationData)) {
      newData = Object.assign({}, this.state.editableIdeationData, {
        backgroundColor: tinycolor(this.state.editableIdeationData.backgroundColor.substr(1)).toHexString().substr(1),
        textColor:  tinycolor(this.state.editableIdeationData.textColor.substr(1)).toHexString().substr(1)
      })
    }

    this.setState({ isLoading: true, showError: false, messageType: null, message: '', errorMessage: '' })
    try {
      let updatedList = this.state.list
      if (this.state.editableIdeationData && this.state.editableIdeationData.id) {
        await axios.post(`/community/api/ideas/${this.state.editableIdeationData?.id}/editIdeaStatus`, { ...newData })
      } else {
        const response = await axios.post('/community/api/ideas/createIdeaStatus', { ...newData })
        this.state.editableIdeationData.id = response.headers.id
      }
      if (this.state.editableIdeationData?.index) {
        updatedList[this.state.editableIdeationData.index] = Object.assign({}, this.state.editableIdeationData)
      }
      this.setState({
        list: updatedList,
        showModal: false,
        isLoading: false,
        message: newData && newData.id ? 'All changes saved!' : `New ideation status '${this.state.editableIdeationData.name}' is added successfully`,
        messageType: 'success'
      })
    } catch (e) {
      this.setState({ isLoading: false, showError: true, errorMessage: e.response.data.description || 'Something went wrong, please try it again later.' })
    }
  }

  enableModal (item: IdeaStatus, idx: number) {
    this.setState({ editableIdeationData: Object.assign({}, item, { index: idx }),
      showModal: true,
      message: '',
      messageType: null
    })
  }

  updateIdeation (newData: IdeaStatus) {
    this.setState({ editableIdeationData: newData })
  }

  shouldDeleteIdeation (idx: number) {
    this.setState({ showConfirmation: true, deleteItem: idx })
  }

  async deleteIdeation () {
    if (typeof this.state.deleteItem !== 'number') {
      return
    }

    this.setState({ isLoading: true, showError: false, messageType: null, message: '', errorMessage: '' })

    const idx: number = this.state.deleteItem
    let updatedList = this.state.list.slice()
    updatedList.splice(idx, 1)
    try {
      await axios.delete(
        `/community/api/ideas/${this.state.list[idx].id}/deleteIdeaStatus`,
        { data: { csrfToken: this.csrfToken } }
      )
      this.setState({
        list: updatedList,
        showConfirmation: false,
        isLoading: false,
        message: `Ideation status '${this.state.list[idx].name}' has been deleted.`,
        messageType: 'success'
      })
    } catch {
      this.setState({ isLoading: false, showError: true })
    }
  }

  render (props: Props,
          { list, newIdeation, showModal, editableIdeationData,
            isLoading, showError, showConfirmation, deleteItem, messageType, message, errorMessage }: State) {
    const colors: Array<string> = list.reduce((allColors: Array<string>, item) => { if (item.backgroundColor && allColors.indexOf(item.backgroundColor) === -1) { allColors.push(item.backgroundColor) } return allColors }, [])

    return (
      <div className='ideation-status-page_container'>
        <Header
          title='Ideation Status'
          disableCancelButton
          disableSaveButton
          customButtons= {[
            <ButtonControl
              newStyle
              variant='brand'
              title='Add new status'
              onClick={() => this.enableModal(this.state.newIdeation, list.length)}
            />
          ]}
        />
        { messageType ? <AlertComponent sticky message={message} messageType={messageType} isVisibleFor={3000} /> : null }
        <IdeationList deleteCallback={this.shouldDeleteIdeation} list={list} colors={colors} openEditIdeationModal={this.enableModal} />
        <Modal isOpen={showModal} onClose={() => this.setState({ showModal: false, showError: false })}>
          <IdeationAddEdit
            title={editableIdeationData && editableIdeationData.index === list.length ? 'Add new status' : 'Edit status'}
            data={editableIdeationData}
            colors={colors}
            onCallback={this.updateIdeation}
            cancelCallback={() => { this.setState({ showModal: false, showError: false }) }}
            isLoading={isLoading}
            showError={showError}
            errorMessage={errorMessage}
            saveCallback={this.saveIdeation}
          />
        </Modal>
        <ConfirmationModal
          isLoading={isLoading}
          showError={showError}
          errorMessage={errorMessage}
          enableModal={showConfirmation}
          title={deleteItem ? `Delete this ideation status '${list[deleteItem] && list[deleteItem].name}'?` : ''}
          message={'All ideas with this status will lose their status.'}
          onClose={() => { this.setState({ showConfirmation: false, showError: false }) }}
          buttons={[
            {
              text: 'Delete',
              callback: this.deleteIdeation,
              class: 'btn btn-danger',
              showLoading: true
            },
            {
              text: 'Cancel',
              callback: () => { this.setState({ showConfirmation: false, showError: false }) },
              class: 'btn btn-default',
              showLoading: false
            }
          ]}
        />
      </div>
    )
  }
}

export default IdeationContainer
